// src/pages/inventario/InventarioPage.jsx
import React, { useState, useEffect } from 'react';
import { get, put, del, getProductImageStatus } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaSearch, FaFilter, FaTimes, FaExclamationTriangle, FaBox, FaTags, FaSpinner, FaImage, FaQrcode, FaLink } from 'react-icons/fa';
import CrearProductoModal from '../../components/modals/CrearProductoModal';
import EditProductoModal from '../../components/modals/EditProductoModal';
import CrearCategoriaModal from '../../components/modals/CrearCategoriaModal';
import EditCategoriaModal from '../../components/modals/EditCategoriaModal';
import useAuthStore from '../../store/authStore';
import QrPdfModal from '../../components/modals/QrPdfModal';
import ShareCatalogoModal from '../../components/modals/ShareCatalogoModal';
import QRCode from 'qrcode';

function InventarioPage() {
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateCategoriaModalOpen, setIsCreateCategoriaModalOpen] = useState(false);
    const [isEditCategoriaModalOpen, setIsEditCategoriaModalOpen] = useState(false);
    const [selectedProducto, setSelectedProducto] = useState(null);
    const [selectedCategoria, setSelectedCategoria] = useState(null);
    const [mostrarInactivos, setMostrarInactivos] = useState(false);
    const [selectedCategoriaFilter, setSelectedCategoriaFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [stockBajo, setStockBajo] = useState(false);
    const [precioMin, setPrecioMin] = useState('');
    const [precioMax, setPrecioMax] = useState('');
    const [loading, setLoading] = useState(false);
    const [marcaId, setMarcaId] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [productImages, setProductImages] = useState({});
    // Mantener un registro de las imágenes que ya están completadas
    const [completedImages, setCompletedImages] = useState(new Set());
    const [imageLoadingStates, setImageLoadingStates] = useState({});
    const [imageCheckTimer, setImageCheckTimer] = useState(null);
    const [showQrModal, setShowQrModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    const itemsPerPage = 12;
    const { user } = useAuthStore();

    // Mover la definición de filteredProductos y paginatedProductos aquí arriba
    const filteredProductos = productos.filter((producto) =>
        producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        producto.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedProductos = filteredProductos.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const totalPages = Math.ceil(filteredProductos.length / itemsPerPage);

    useEffect(() => {
        fetchMarcaId();
    }, []);

    useEffect(() => {
        if (marcaId) {
            fetchCategorias();
            fetchProductos();
        }
    }, [marcaId, mostrarInactivos, selectedCategoriaFilter, currentPage, stockBajo, precioMin, precioMax]);

    // Función para verificar el estado de las imágenes pendientes
    const checkPendingImages = async () => {
        const pendingProducts = Object.entries(imageLoadingStates)
            .filter(([_, state]) => state === 'pending')
            .map(([id]) => id);

        if (pendingProducts.length === 0) {
            if (imageCheckTimer) {
                clearTimeout(imageCheckTimer);
                setImageCheckTimer(null);
            }
            return;
        }

        const imagePromises = pendingProducts.map(async (productId) => {
            try {
                const imageStatus = await getProductImageStatus(productId);

                if (imageStatus.status === 'completed' && imageStatus.imageUrl) {
                    setProductImages(prev => ({ ...prev, [productId]: imageStatus.imageUrl }));
                    setImageLoadingStates(prev => ({ ...prev, [productId]: 'completed' }));
                    return true;
                } else if (imageStatus.status === 'failed') {
                    setImageLoadingStates(prev => ({ ...prev, [productId]: 'failed' }));
                    return true;
                }
                return false;
            } catch (error) {
                console.error(`Error checking image status for product ${productId}:`, error);
                setImageLoadingStates(prev => ({ ...prev, [productId]: 'failed' }));
                return true;
            }
        });

        await Promise.all(imagePromises);

        // Programar la siguiente verificación solo si aún hay imágenes pendientes
        const timer = setTimeout(() => checkPendingImages(), 5000);
        setImageCheckTimer(timer);
    };

    // Efecto para inicializar el estado de carga de imágenes cuando cambian los productos
    useEffect(() => {
        const newImageStates = {};
        paginatedProductos.forEach(producto => {
            if (!imageLoadingStates[producto._id]) {
                newImageStates[producto._id] = 'pending';
            }
        });

        if (Object.keys(newImageStates).length > 0) {
            setImageLoadingStates(prev => ({ ...prev, ...newImageStates }));
        }
    }, [paginatedProductos]);

    // Efecto para manejar la verificación de imágenes pendientes
    useEffect(() => {
        const pendingExists = Object.values(imageLoadingStates).some(state => state === 'pending');
        if (pendingExists && !imageCheckTimer) {
            checkPendingImages();
        }

        return () => {
            if (imageCheckTimer) {
                clearTimeout(imageCheckTimer);
            }
        };
    }, [imageLoadingStates, imageCheckTimer]);

    const fetchMarcaId = async () => {
        try {
            const marcas = await get(`marcas/user/${user.userId}`);
            if (marcas.length > 0) {
                const marcaId = marcas[0]._id;
                setMarcaId(marcaId);
            }
        } catch (error) {
            console.error('Error fetching marca:', error);
        }
    };

    const fetchProductos = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();

            // Enviar el parámetro activo como booleano
            params.append('activo', mostrarInactivos ? 'false' : 'true');

            if (selectedCategoriaFilter !== 'all') params.append('categoria', selectedCategoriaFilter);
            if (searchTerm) params.append('busqueda', searchTerm);
            if (stockBajo) params.append('stockBajo', 'true');
            if (precioMin) params.append('precioMin', precioMin);
            if (precioMax) params.append('precioMax', precioMax);

            // Actualizar la ruta para usar el nuevo endpoint
            const url = `productos/${marcaId}/lista?${params.toString()}`;
            const response = await get(url);

            // Asegurarse de que response es un array
            if (Array.isArray(response)) {
                setProductos(response);
            } else if (response.data && Array.isArray(response.data)) {
                // Si la respuesta viene envuelta en un objeto con propiedad data
                setProductos(response.data);
            } else {
                console.error('Invalid response format:', response);
                setProductos([]);
            }
        } catch (error) {
            console.error('Error fetching productos:', error);
            // Solo mostrar error si no es un error de servidor interno
            if (error.message && !error.message.includes('Internal server error')) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar los productos. Intente de nuevo.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            }
            setProductos([]); // Establecer array vacío en caso de error
        } finally {
            setLoading(false);
        }
    };

    const fetchCategorias = async () => {
        try {
            const response = await get(`productos/${marcaId}/categorias?activa=true`);
            setCategorias(response);
        } catch (error) {
            console.error('Error fetching categorias:', error);
            // No mostrar error al usuario si es un error de servidor, solo log
            if (error.message && !error.message.includes('Internal server error')) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar las categorías. Intente de nuevo.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            }
        }
    };

    const limpiarFiltros = () => {
        setSearchTerm('');
        setSelectedCategoriaFilter('all');
        setStockBajo(false);
        setPrecioMin('');
        setPrecioMax('');
        setCurrentPage(1);
    };

    const handleToggleActivo = async (id) => {
        try {
            await put(`productos/${marcaId}/${id}/toggle-activo`);
            fetchProductos();
            Swal.fire({
                title: 'Éxito',
                text: 'El estado del producto se ha actualizado correctamente.',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo actualizar el estado del producto.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const handleDeleteProducto = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: "Esta acción no se puede deshacer",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await del(`productos/${marcaId}/${id}`);
                fetchProductos();
                Swal.fire(
                    'Eliminado',
                    'El producto ha sido eliminado.',
                    'success'
                );
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message,
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            }
        }
    };

    const handleToggleCategoriaActiva = async (id) => {
        try {
            await put(`productos/${marcaId}/categorias/${id}/toggle-activa`);
            fetchCategorias();
            Swal.fire({
                title: 'Éxito',
                text: 'El estado de la categoría se ha actualizado correctamente.',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo actualizar el estado de la categoría.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const handleDeleteCategoria = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: "Esta acción eliminará la categoría y todos los productos asociados",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await del(`productos/${marcaId}/categorias/${id}`);
                fetchCategorias();
                fetchProductos();
                Swal.fire(
                    'Eliminado',
                    'La categoría ha sido eliminada.',
                    'success'
                );
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message,
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            }
        }
    };

    const getCategoriaNombre = (categoriaId) => {
        // Si categoriaId es un objeto, extraer el _id
        const idToSearch = typeof categoriaId === 'object' && categoriaId !== null ? categoriaId._id : categoriaId;

        const categoria = categorias.find(cat => cat._id === idToSearch);
        return categoria ? categoria.nombre : 'Sin categoría';
    };

    const isStockBajo = (producto) => {
        return producto.cantidad <= producto.stockMinimo;
    };

    const handleProductoCreated = async () => {
        try {
            // Asegurarse de que estamos mostrando productos activos
            setMostrarInactivos(false);
            // Limpiar los filtros y volver a la primera página
            setCurrentPage(1);
            setSearchTerm('');
            setSelectedCategoriaFilter('all');
            setStockBajo(false);
            setPrecioMin('');
            setPrecioMax('');

            // Esperar un momento antes de recargar para dar tiempo al backend
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Recargar los productos
            await fetchProductos();
        } catch (error) {
            console.error('Error refreshing products:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudieron cargar los productos. Intente recargar la página.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const generateAndDownloadQR = async () => {
        try {
            const baseUrl = window.location.origin;
            const catalogUrl = `${baseUrl}/productos/${marcaId}`;

            // Primero mostrar mensaje informativo
            await Swal.fire({
                title: 'Generando QR para catálogo público',
                html: `
                    <div class="text-left">
                        <p class="mb-2">Este QR permitirá a tus clientes acceder a:</p>
                        <ul class="list-disc pl-5 mb-4">
                            <li>Catálogo completo de productos</li>
                            <li>Precios actualizados</li>
                            <li>Información detallada</li>
                        </ul>
                        <p class="text-sm text-gray-600">URL del catálogo: ${catalogUrl}</p>
                    </div>
                `,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Descargar QR',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#4F46E5',
            });

            // Generar el QR como URL de datos
            const qrDataUrl = await QRCode.toDataURL(catalogUrl, {
                errorCorrectionLevel: 'H',
                margin: 1,
                width: 300,
                color: {
                    dark: '#000000',
                    light: '#ffffff'
                }
            });

            // Crear un enlace temporal para la descarga
            const link = document.createElement('a');
            link.href = qrDataUrl;
            link.download = `catalogo-productos-qr.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Mostrar mensaje de éxito
            Swal.fire({
                title: '¡QR generado!',
                text: 'Comparte este QR con tus clientes para que accedan a tu catálogo',
                icon: 'success',
                toast: true,
                position: 'bottom-end',
                showConfirmButton: false,
                timer: 3000,
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo generar el código QR',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">

                {/* Header Principal */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                                Gestión de Inventario
                            </h1>
                            <p className="text-sm sm:text-base text-gray-600">
                                Administra tus productos y categorías de manera eficiente
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => setIsCreateModalOpen(true)}
                                disabled={categorias.length === 0}
                                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center text-sm ${categorias.length === 0
                                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                                    }`}
                            >
                                <FaPlus className="mr-2" />
                                Crear Producto
                            </button>
                            <button
                                onClick={() => setIsCreateCategoriaModalOpen(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center text-sm"
                            >
                                <FaTags className="mr-2" />
                                Crear Categoría
                            </button>
                            {/* Sección de compartir catálogo */}
                            <div className="relative group">
                                <div className="flex gap-2">
                                    <button
                                        onClick={generateAndDownloadQR}
                                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                                        title="Generar código QR para acceso al catálogo público"
                                    >
                                        <FaQrcode className="mr-2" />
                                        Descargar QR
                                    </button>
                                    <button
                                        onClick={() => setShowShareModal(true)}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                                        title="Copiar enlace del catálogo público"
                                    >
                                        <FaLink className="mr-2" />
                                        Copiar Link
                                    </button>
                                </div>
                                {/* Tooltip informativo */}
                                <div className="absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-sm rounded-lg p-3 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                    <div className="relative">
                                        <div className="absolute bottom-0 left-4 transform translate-y-full">
                                            <div className="border-8 border-transparent border-t-gray-900"></div>
                                        </div>
                                        <p className="font-medium mb-1">Catálogo Público</p>
                                        <p className="text-gray-300 text-xs">
                                            Genera un QR o comparte el enlace para que tus clientes accedan a tu catálogo de productos actualizado
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mensaje cuando no hay categorías */}
                {categorias.length === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 sm:p-6 shadow-sm mb-6 sm:mb-8">
                        <div className="flex">
                            <FaExclamationTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-1" />
                            <div className="ml-3">
                                <h3 className="text-base sm:text-lg font-semibold text-yellow-800 mb-2">
                                    No hay categorías configuradas
                                </h3>
                                <p className="text-sm text-yellow-700 leading-relaxed">
                                    Para poder crear productos, primero debe crear al menos una categoría.
                                    Haga clic en "Crear Categoría" para comenzar.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Controles de filtros */}
                <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-100 mb-6 sm:mb-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => setMostrarInactivos(!mostrarInactivos)}
                                className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center text-sm ${mostrarInactivos
                                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-md'
                                    : 'bg-red-600 text-white hover:bg-red-700 shadow-md'
                                    }`}
                            >
                                {mostrarInactivos ? <FaEye className="mr-2" /> : <FaEyeSlash className="mr-2" />}
                                {mostrarInactivos ? 'Ver Activos' : 'Ver Inactivos'}
                            </button>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center text-sm"
                            >
                                <FaFilter className="mr-2" />
                                Filtros
                            </button>
                        </div>

                        {/* Búsqueda rápida */}
                        <div className="relative w-full lg:max-w-md">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Buscar productos..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                            />
                        </div>
                    </div>

                    {/* Filtros avanzados expandibles */}
                    {showFilters && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Categoría
                                    </label>
                                    {categorias.length === 0 ? (
                                        <div className="w-full px-3 py-2 border border-orange-300 rounded-lg bg-orange-50 text-orange-600 text-sm">
                                            No hay categorías disponibles
                                        </div>
                                    ) : (
                                        <select
                                            value={selectedCategoriaFilter}
                                            onChange={(e) => setSelectedCategoriaFilter(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                                        >
                                            <option value="all">Todas las categorías</option>
                                            {categorias.map((categoria) => (
                                                <option key={categoria._id} value={categoria._id}>
                                                    {categoria.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Precio mínimo
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="0.00"
                                        value={precioMin}
                                        onChange={(e) => setPrecioMin(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Precio máximo
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="999.99"
                                        value={precioMax}
                                        onChange={(e) => setPrecioMax(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                                    />
                                </div>
                                <div className="flex items-end">
                                    <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={stockBajo}
                                            onChange={(e) => setStockBajo(e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                        />
                                        <span>Solo stock bajo</span>
                                    </label>
                                </div>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={limpiarFiltros}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center text-sm"
                                >
                                    <FaTimes className="mr-2" />
                                    Limpiar Filtros
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabla de productos */}
                <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-6 sm:mb-8">
                    <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
                            <FaBox className="mr-2" />
                            Productos ({filteredProductos.length})
                        </h3>
                    </div>

                    {/* Vista de tabla para desktop */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Producto</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Categoría</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Precio</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Stock</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Estado</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-12">
                                            <div className="flex flex-col items-center justify-center text-gray-500">
                                                <FaSpinner className="h-8 w-8 mb-2 animate-spin" />
                                                <span>Cargando productos...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginatedProductos.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-12">
                                            <div className="flex flex-col items-center justify-center">
                                                <FaBox className="h-12 w-12 text-gray-300 mb-4" />
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron productos</h3>
                                                <p className="text-gray-500 text-sm">
                                                    {searchTerm || selectedCategoriaFilter !== 'all' || stockBajo || precioMin || precioMax
                                                        ? 'Intenta ajustar los filtros de búsqueda'
                                                        : 'Crea tu primer producto para comenzar'
                                                    }
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedProductos.map((producto) => (
                                        <tr key={producto._id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                                            <td className="px-6 py-4">
                                                <div className="flex items-start space-x-3">
                                                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200">
                                                        {productImages[producto._id] ? (
                                                            <img
                                                                src={productImages[producto._id]}
                                                                alt={producto.nombre}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                                                <FaImage className="w-6 h-6 text-gray-400" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="space-y-2">
                                                        <div className="font-semibold text-gray-900 text-sm">{producto.nombre}</div>
                                                        {producto.descripcion && (
                                                            <div className="text-gray-500 text-xs line-clamp-2">
                                                                {producto.descripcion}
                                                            </div>
                                                        )}
                                                        {producto.etiquetas && producto.etiquetas.length > 0 && (
                                                            <div className="flex flex-wrap gap-1">
                                                                {producto.etiquetas.slice(0, 3).map((tag, index) => (
                                                                    <span key={index} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                                {producto.etiquetas.length > 3 && (
                                                                    <span className="text-xs text-gray-500 px-1">
                                                                        +{producto.etiquetas.length - 3}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-1">
                                                    <span className="font-medium text-gray-900 text-sm">
                                                        {getCategoriaNombre(producto.categoria)}
                                                    </span>
                                                    {producto.subcategoria && (
                                                        <div className="text-xs text-gray-500">
                                                            {producto.subcategoria}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="font-semibold text-gray-900 text-sm">
                                                    ${producto.precio.toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className={`space-y-1 ${isStockBajo(producto) ? 'text-red-600' : 'text-green-600'}`}>
                                                    <span className="font-semibold text-sm">
                                                        {producto.cantidad}
                                                    </span>
                                                    {producto.stockMinimo > 0 && (
                                                        <div className="text-xs text-gray-500">
                                                            Mín: {producto.stockMinimo}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${producto.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {producto.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedProducto(producto);
                                                            setIsEditModalOpen(true);
                                                        }}
                                                        className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-200 text-xs"
                                                        title="Editar producto"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleActivo(producto._id)}
                                                        className={`p-2 rounded-lg transition-colors duration-200 text-xs ${producto.activo
                                                            ? 'bg-red-500 text-white hover:bg-red-600'
                                                            : 'bg-green-500 text-white hover:bg-green-600'
                                                            }`}
                                                        title={producto.activo ? 'Desactivar' : 'Activar'}
                                                    >
                                                        {producto.activo ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteProducto(producto._id)}
                                                        className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 text-xs"
                                                        title="Eliminar producto"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Vista de cards para móviles y tablets */}
                    <div className="lg:hidden">
                        {loading ? (
                            <div className="text-center py-12">
                                <div className="flex flex-col items-center justify-center text-gray-500">
                                    <FaSpinner className="h-8 w-8 mb-2 animate-spin" />
                                    <span>Cargando productos...</span>
                                </div>
                            </div>
                        ) : paginatedProductos.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="flex flex-col items-center justify-center">
                                    <FaBox className="h-12 w-12 text-gray-300 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron productos</h3>
                                    <p className="text-gray-500 text-sm">
                                        {searchTerm || selectedCategoriaFilter !== 'all' || stockBajo || precioMin || precioMax
                                            ? 'Intenta ajustar los filtros de búsqueda'
                                            : 'Crea tu primer producto para comenzar'
                                        }
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 space-y-4">
                                {paginatedProductos.map((producto) => (
                                    <div key={producto._id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        {/* Header del producto */}
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-start space-x-3 mb-3">
                                                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200">
                                                        {productImages[producto._id] ? (
                                                            <img
                                                                src={productImages[producto._id]}
                                                                alt={producto.nombre}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                                                <FaImage className="w-6 h-6 text-gray-400" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 text-base mb-1">{producto.nombre}</h4>
                                                        <div className="flex items-center space-x-3 text-sm">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${producto.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                {producto.activo ? 'Activo' : 'Inactivo'}
                                                            </span>
                                                            <span className="text-gray-600">
                                                                {getCategoriaNombre(producto.categoria)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Descripción */}
                                                {producto.descripcion && (
                                                    <div className="text-gray-600 text-sm mb-3 line-clamp-2">
                                                        {producto.descripcion}
                                                    </div>
                                                )}

                                                {/* Etiquetas */}
                                                {producto.etiquetas && producto.etiquetas.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mb-3">
                                                        {producto.etiquetas.slice(0, 3).map((tag, index) => (
                                                            <span key={index} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                                {tag}
                                                            </span>
                                                        ))}
                                                        {producto.etiquetas.length > 3 && (
                                                            <span className="text-xs text-gray-500 px-1">
                                                                +{producto.etiquetas.length - 3}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Información de precio y stock */}
                                                <div className="flex justify-between items-center">
                                                    <div className="text-left">
                                                        <div className="text-lg font-bold text-gray-900">
                                                            ${producto.precio.toFixed(2)}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            {producto.subcategoria && (
                                                                <span className="block">{producto.subcategoria}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className={`text-lg font-bold ${isStockBajo(producto) ? 'text-red-600' : 'text-green-600'}`}>
                                                            {producto.cantidad}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            Stock
                                                            {producto.stockMinimo > 0 && (
                                                                <span className="block text-xs">Mín: {producto.stockMinimo}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <button
                                                    onClick={() => {
                                                        setSelectedProducto(producto);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-200"
                                                    title="Editar producto"
                                                >
                                                    <FaEdit className="text-xs" />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleActivo(producto._id)}
                                                    className={`p-2 rounded-lg transition-colors duration-200 ${producto.activo
                                                        ? 'bg-red-500 text-white hover:bg-red-600'
                                                        : 'bg-green-500 text-white hover:bg-green-600'
                                                        }`}
                                                    title={producto.activo ? 'Desactivar' : 'Activar'}
                                                >
                                                    {producto.activo ? <FaEyeSlash className="text-xs" /> : <FaEye className="text-xs" />}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteProducto(producto._id)}
                                                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                                                    title="Eliminar producto"
                                                >
                                                    <FaTrash className="text-xs" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Paginación */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center space-x-2 p-4 sm:p-6 border-t border-gray-200">
                            {Array.from({ length: totalPages }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentPage(index + 1)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${currentPage === index + 1
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Tabla de categorías */}
                <div className="bg-white rounded-xl shadow-md border border-gray-100">
                    <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
                            <FaTags className="mr-2" />
                            Categorías ({categorias.length})
                        </h3>
                    </div>

                    {/* Vista de tabla para desktop */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Nombre</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Descripción</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Estado</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categorias.map((categoria) => (
                                    <tr key={categoria._id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150">
                                        <td className="px-6 py-4">
                                            <span className="font-semibold text-gray-900 text-sm">
                                                {categoria.nombre}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-gray-600 text-sm">
                                                {categoria.descripcion || 'Sin descripción'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoria.activa ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {categoria.activa ? 'Activa' : 'Inactiva'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedCategoria(categoria);
                                                        setIsEditCategoriaModalOpen(true);
                                                    }}
                                                    className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-200"
                                                    title="Editar categoría"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleCategoriaActiva(categoria._id)}
                                                    className={`p-2 rounded-lg transition-colors duration-200 ${categoria.activa
                                                        ? 'bg-red-500 text-white hover:bg-red-600'
                                                        : 'bg-green-500 text-white hover:bg-green-600'
                                                        }`}
                                                    title={categoria.activa ? 'Desactivar' : 'Activar'}
                                                >
                                                    {categoria.activa ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCategoria(categoria._id)}
                                                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                                                    title="Eliminar categoría"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Vista de cards para móviles y tablets */}
                    <div className="lg:hidden">
                        <div className="p-4 space-y-4">
                            {categorias.map((categoria) => (
                                <div key={categoria._id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-gray-900 text-base mb-2">{categoria.nombre}</h4>
                                            <p className="text-gray-600 text-sm mb-3">
                                                {categoria.descripcion || 'Sin descripción'}
                                            </p>
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoria.activa ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {categoria.activa ? 'Activa' : 'Inactiva'}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <button
                                                onClick={() => {
                                                    setSelectedCategoria(categoria);
                                                    setIsEditCategoriaModalOpen(true);
                                                }}
                                                className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors duration-200"
                                                title="Editar categoría"
                                            >
                                                <FaEdit className="text-xs" />
                                            </button>
                                            <button
                                                onClick={() => handleToggleCategoriaActiva(categoria._id)}
                                                className={`p-2 rounded-lg transition-colors duration-200 ${categoria.activa
                                                    ? 'bg-red-500 text-white hover:bg-red-600'
                                                    : 'bg-green-500 text-white hover:bg-green-600'
                                                    }`}
                                                title={categoria.activa ? 'Desactivar' : 'Activar'}
                                            >
                                                {categoria.activa ? <FaEyeSlash className="text-xs" /> : <FaEye className="text-xs" />}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCategoria(categoria._id)}
                                                className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                                                title="Eliminar categoría"
                                            >
                                                <FaTrash className="text-xs" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modales */}
            {isCreateModalOpen && (
                <CrearProductoModal
                    isOpen={isCreateModalOpen}
                    onClose={() => {
                        setIsCreateModalOpen(false);
                    }}
                    onProductoCreated={handleProductoCreated}
                />
            )}
            {isEditModalOpen && selectedProducto && (
                <EditProductoModal
                    isOpen={isEditModalOpen}
                    producto={selectedProducto}
                    onClose={() => {
                        setIsEditModalOpen(false);
                    }}
                    onProductoUpdated={() => {
                        limpiarFiltros();
                        fetchProductos();
                    }}
                />
            )}
            {isCreateCategoriaModalOpen && (
                <CrearCategoriaModal
                    isOpen={isCreateCategoriaModalOpen}
                    onClose={() => setIsCreateCategoriaModalOpen(false)}
                    onCategoriaCreated={fetchCategorias}
                />
            )}
            {isEditCategoriaModalOpen && selectedCategoria && (
                <EditCategoriaModal
                    isOpen={isEditCategoriaModalOpen}
                    categoria={selectedCategoria}
                    onClose={() => setIsEditCategoriaModalOpen(false)}
                    onCategoriaUpdated={fetchCategorias}
                />
            )}
            {showQrModal && (
                <QrPdfModal
                    tiendaId={marcaId}
                    onClose={() => setShowQrModal(false)}
                    type="productos"
                    title="QR Catálogo de Productos"
                    description="Comparte este QR para que tus clientes puedan ver tu catálogo de productos"
                    path={`/productos/${marcaId}`}
                />
            )}
            {showShareModal && (
                <ShareCatalogoModal
                    isOpen={showShareModal}
                    onClose={() => setShowShareModal(false)}
                    marcaId={marcaId}
                />
            )}
        </div>
    );
}

export default InventarioPage;
